import {
    Drawer,
    TextField,
    FormControl,
    FormHelperText,
    Button,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import Title from "@/shared/components/Title/Title";
import CloseCross from "@/shared/components/CloseCross/CloseCross";
import { RootState } from "@/store/store";
import { inputStyles } from "@/utils/constants";
import CustomButton from "@/shared/components/Button/Button";
import useDownloadPhotos from "@/hooks/useDownloadPhotos";
import { addingButtonStyles } from "@/utils/constants";

import { ISearchDrawerProps } from "@/shared/interfaces/ui";
import { IAddingPhotos } from "@/shared/interfaces/patientPage";

import "./PatientDetailsPageFormDrawer.scss";

export default function PatientDetailsPageFormDrawer({
    open,
    toggleDrawer,
}: ISearchDrawerProps) {
    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );
    const { handleDownloadPfotos } = useDownloadPhotos();
    const {
        handleSubmit,
        formState: { errors },
        reset,
        register,
        watch,
    } = useForm<{
        title: string;
        description: string;
        file: FileList | null;
    }>({
        defaultValues: {
            title: "",
            description: "",
            file: null,
        },
    });
    const files = watch("file");

    const onSubmit = async (data: IAddingPhotos): Promise<void> => {
        await handleDownloadPfotos(data);
        reset();
        toggleDrawer(false);
    };

    return (
        <Drawer
            open={open}
            onClose={() => {
                if (!isLocalLoading) {
                    toggleDrawer(false);
                    reset();
                }
            }}
            anchor='right'
        >
            <div className='drawer_container'>
                <div className='drawer_title_container drawer_app_item'>
                    <Title tag='h2' text={"Додати фото"} />
                    <CloseCross
                        onClick={() => {
                            toggleDrawer(false);
                            reset();
                        }}
                    />
                </div>
                <form
                    className='drawer_container_create_entity'
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className='drawer_form_container'>
                        {" "}
                        <TextField
                            type='text'
                            id='title'
                            label='Назва документа'
                            {...register("title", {
                                required: "Назва документа є обов'язковою",
                                minLength: {
                                    value: 5,
                                    message: "Мінімум 5 символи",
                                },
                                maxLength: {
                                    value: 120,
                                    message: "Максимум 120 символів",
                                },
                            })}
                            variant='outlined'
                            sx={{
                                ...inputStyles.root,
                                marginBottom: 2,
                            }}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            fullWidth
                            inputProps={{ maxLength: 120 }}
                        />
                        <TextField
                            type='text'
                            id='description'
                            label='Опис документа'
                            variant='outlined'
                            sx={{
                                ...inputStyles.root,
                                marginBottom: 2,
                            }}
                            error={!!errors.description}
                            helperText={errors.description?.message}
                            fullWidth
                            {...register("description", {
                                maxLength: {
                                    value: 200,
                                    message: "Максимум 200 символів",
                                },
                            })}
                            inputProps={{ maxLength: 200 }}
                        />
                        <FormControl
                            sx={{ marginBottom: 2 }}
                            fullWidth
                            error={!!errors.file}
                        >
                            <input
                                id='file'
                                type='file'
                                hidden
                                multiple
                                {...register("file", {
                                    validate: {
                                        maxFiles: files =>
                                            (files?.length ?? 0) <= 12 ||
                                            "Максимум 12 файлів",
                                        lessThan10MB: files =>
                                            Array.from(files || []).every(
                                                file =>
                                                    file.size <
                                                    10 * 1024 * 1024,
                                            ) ||
                                            "Кожен файл має бути менше 10MB",
                                        acceptedFormats: files =>
                                            Array.from(files || []).every(
                                                file =>
                                                    [
                                                        "image/jpeg",
                                                        "image/png",
                                                        "application/pdf",
                                                    ].includes(file.type),
                                            ) || "Тільки JPEG, PNG, або PDF",
                                    },
                                })}
                            />

                            <label htmlFor='file' style={{ width: "100%" }}>
                                <Button
                                    variant='outlined'
                                    component='span'
                                    sx={addingButtonStyles}
                                >
                                    Вибрати файли
                                </Button>
                            </label>

                            <FormHelperText>
                                {errors.file?.message}
                            </FormHelperText>

                            {files && files.length > 0 && (
                                <ul className='file_listing'>
                                    {Array.from(files).map((file, index) => (
                                        <li
                                            key={index}
                                            className='file__li_item'
                                        >
                                            <p>{index + 1}.</p>
                                            <p className='file__li_item_title'>
                                                {file.name.length > 30
                                                    ? `${file.name.slice(
                                                          0,
                                                          30,
                                                      )}...`
                                                    : file.name}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </FormControl>
                    </div>
                    <CustomButton
                        text={"Додати фото"}
                        variant={"approved"}
                        type='submit'
                        disabled={isLocalLoading}
                        isLoading={isLocalLoading}
                    />
                </form>
            </div>
        </Drawer>
    );
}
